"use client";

import { useCallback, useId, useState } from "react";
import type {
  EffectCard as EffectCardData,
  EffectEntry,
} from "@/lib/core/schemas/effect-card";
import { cn } from "@/lib/utils";

// ── Outcome label mapping ───────────────────────────────────────────
// Frontend-only mapping per docs/superpowers/specs/2026-04-20-effect-cards-design.md.
// The backend emits raw outcome IDs; display labels live here.
const OUTCOME_LABELS: Record<string, string> = {
  hit: "Hit",
  miss: "Miss",
  crit: "Critical Hit",
  fumble: "Fumble",
  save_pass: "Save Passed",
  save_fail: "Save Failed",
  success: "Success",
};

function titleCase(id: string): string {
  return id
    .split("_")
    .map((s) => (s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1)))
    .join(" ");
}

function labelFor(outcomeId: string): string {
  return OUTCOME_LABELS[outcomeId] ?? titleCase(outcomeId);
}

// Outcome families drive the accent/border colour, mirroring ActionRollCard's
// palette so the two card types feel like siblings.
type OutcomeFamily = "hit" | "crit" | "miss" | "fumble" | "neutral";

function outcomeFamily(outcomeId: string): OutcomeFamily {
  if (outcomeId === "crit") {
    return "crit";
  }
  if (outcomeId === "fumble") {
    return "fumble";
  }
  if (
    outcomeId === "hit" ||
    outcomeId === "save_pass" ||
    outcomeId === "success"
  ) {
    return "hit";
  }
  if (outcomeId === "miss" || outcomeId === "save_fail") {
    return "miss";
  }
  return "neutral";
}

const FAMILY_BORDER: Record<OutcomeFamily, string> = {
  hit: "border-green-600/60",
  crit: "border-yellow-400/70",
  miss: "border-zinc-600/60",
  fumble: "border-red-700/70",
  neutral: "border-border",
};

const FAMILY_ACCENT: Record<OutcomeFamily, string> = {
  hit: "text-green-400",
  crit: "text-yellow-300",
  miss: "text-zinc-400",
  fumble: "text-red-400",
  neutral: "text-card-foreground",
};

// ── Roll formatting ─────────────────────────────────────────────────

function formatModifier(modifier: number): string {
  if (modifier > 0) {
    return ` +${modifier}`;
  }
  if (modifier < 0) {
    return ` ${modifier}`;
  }
  return "";
}

function formatDiceFaces(
  rolls: readonly number[],
  rollKind: "normal" | "advantage" | "disadvantage",
  total: number
): string {
  if (rollKind === "normal") {
    const face = rolls[0];
    return typeof face === "number" ? String(face) : String(total);
  }
  if (rolls.length === 0) {
    return String(total);
  }
  const kept =
    rollKind === "advantage" ? Math.max(...rolls) : Math.min(...rolls);
  return `${rolls.join(", ")} (${rollKind}; kept ${kept})`;
}

function formatCompactSummary(card: EffectCardData): string {
  const outcome = labelFor(card.outcomeId);
  if (!card.roll) {
    return `${card.actionName} — ${outcome}`;
  }
  const { total, versus } = card.roll;
  if (!versus) {
    return `${card.actionName} — ${outcome} (${total})`;
  }
  return `${card.actionName} — ${outcome} (${total} vs ${versus.kind} ${versus.value})`;
}

function compactSuffixForFirstEffect(effects: readonly EffectEntry[]): string {
  const first = effects[0];
  if (!first) {
    return "";
  }
  if (first.type === "damage") {
    return ` · ${first.amount} ${first.damageType}`;
  }
  if (first.type === "healing") {
    return ` · +${first.amount} healed`;
  }
  return "";
}

function formatExpandedRoll(card: EffectCardData): string | null {
  if (!card.roll) {
    return null;
  }
  const { total, rolls, rollKind, modifier, versus } = card.roll;
  const diceStr = formatDiceFaces(rolls, rollKind, total);
  const modStr = formatModifier(modifier);
  const vs = versus ? ` vs ${versus.kind} ${versus.value}` : "";
  return `${diceStr}${modStr} = ${total}${vs}`;
}

// ── Effect entry rendering ──────────────────────────────────────────

export function renderEntry(entry: EffectEntry): string {
  switch (entry.type) {
    case "damage": {
      const notation = entry.notation ? ` (${entry.notation})` : "";
      return `${entry.amount} ${entry.damageType} damage${notation}`;
    }
    case "healing": {
      const notation = entry.notation ? ` (${entry.notation})` : "";
      return `Heal ${entry.amount}${notation}`;
    }
    case "status_applied":
      return `Applied ${entry.statusId}`;
    case "status_removed":
      return `Removed ${entry.statusId}`;
    case "resource_modified": {
      const sign = entry.amount >= 0 ? `+${entry.amount}` : `${entry.amount}`;
      return `${entry.resourceId} ${sign}`;
    }
    default: {
      // Exhaustiveness check: if a new EffectEntry type is added, TS will
      // flag this branch as reachable, forcing an update here.
      const _exhaustive: never = entry;
      return String(_exhaustive);
    }
  }
}

export { labelFor };

// ── Component ───────────────────────────────────────────────────────

export function EffectCard({ card }: { card: EffectCardData }) {
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();

  const toggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const family = outcomeFamily(card.outcomeId);
  const borderClass = FAMILY_BORDER[family];
  const accentClass = FAMILY_ACCENT[family];

  const summary = formatCompactSummary(card);
  const summarySuffix = compactSuffixForFirstEffect(card.effects);
  const expandedRoll = formatExpandedRoll(card);
  const showActorLine = card.actorName !== card.targetName;

  return (
    <div
      className={cn(
        "my-1.5 rounded-md border bg-card text-card-foreground",
        // Disable colour/border transitions under reduced-motion; reduce-motion
        // users get an instant state swap instead of any animation.
        "transition-colors motion-reduce:transition-none",
        borderClass
      )}
    >
      <button
        aria-controls={panelId}
        aria-expanded={expanded}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm",
          "hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "rounded-md"
        )}
        onClick={toggle}
        type="button"
      >
        <span
          aria-hidden="true"
          className={cn(
            "font-mono text-xs uppercase tracking-wider",
            accentClass
          )}
        >
          {labelFor(card.outcomeId)}
        </span>
        <span className="flex-1 truncate text-muted-foreground">
          {summary}
          {!expanded && summarySuffix && (
            <span className="text-muted-foreground/80">{summarySuffix}</span>
          )}
        </span>
        <span
          aria-hidden="true"
          className={cn(
            "shrink-0 text-muted-foreground text-xs transition-transform motion-reduce:transition-none",
            expanded && "rotate-180"
          )}
        >
          {"\u25BE"}
        </span>
      </button>

      {expanded && (
        <div
          className="flex flex-col gap-1.5 border-border border-t px-3 py-2 text-sm"
          id={panelId}
        >
          <div className="font-medium text-card-foreground">
            {card.actionName}
          </div>
          {showActorLine && (
            <div className="text-muted-foreground text-xs">
              {card.actorName}
              <span className="mx-1 text-muted-foreground/60">{"\u2192"}</span>
              {card.targetName}
            </div>
          )}
          {expandedRoll && (
            <div className="text-muted-foreground text-xs">
              <span className="text-muted-foreground/70">Roll: </span>
              <span className="font-mono text-card-foreground tabular-nums">
                {expandedRoll}
              </span>
            </div>
          )}
          <div className="text-muted-foreground text-xs">
            <span className="text-muted-foreground/70">Result: </span>
            <span className={cn("font-medium", accentClass)}>
              {labelFor(card.outcomeId)}
            </span>
          </div>
          {card.effects.length > 0 && (
            <ul className="mt-0.5 flex flex-col gap-0.5 text-xs">
              {card.effects.map((entry, i) => (
                <li
                  className="text-card-foreground"
                  // eslint-disable-next-line react/no-array-index-key -- entries have no stable id; order is the only identity
                  key={`${entry.type}-${i}`}
                >
                  {renderEntry(entry)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
